import { useState } from 'react';
import { Alert, App, Button, Card, Descriptions, Result, Space, Spin, Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import type { UpdateDoohScreenBody } from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';
import { useAuth } from '@/features/auth/context/AuthContext';
import { canManageDoohScreens } from '@/layouts/navigation';
import { formatDateTime, formatResolution, ORIENTATION_LABELS } from '../utils/format';
import { doohApi } from '../api/doohApi';
import { DoohScreenStatusBadge } from '../components/DoohBadges';
import { ScreenConfigForm } from '../components/ScreenConfigForm';
import { useDoohScreen } from '../hooks/useDoohScreen';

/**
 * Detalle y configuración de una pantalla DOOH (TG-59, `/dooh/pantallas/:screenId`).
 * Muestra el estado/último contacto y edita la config (nombre, ubicación/aliado,
 * resolución, orientación, zona horaria). Read-only para roles sin permiso.
 */
export function ScreenDetailPage() {
  const { screenId } = useParams<{ screenId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { message } = App.useApp();
  const detail = useDoohScreen(screenId);
  const [saving, setSaving] = useState(false);

  const canManage = canManageDoohScreens(user?.role);
  const goBack = () => navigate('/dooh/pantallas');

  const save = async (body: UpdateDoohScreenBody) => {
    if (!screenId) return;
    setSaving(true);
    try {
      await doohApi.updateScreen(screenId, body);
      message.success('Configuración guardada.');
      detail.refresh();
    } catch (caught) {
      message.error(
        caught instanceof ApiError ? caught.message : 'No se pudo guardar la configuración.',
      );
    } finally {
      setSaving(false);
    }
  };

  if (detail.loading) {
    return (
      <div className="dooh-page" style={{ display: 'grid', placeItems: 'center', minHeight: 240 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (detail.notFound) {
    return (
      <Result
        status="404"
        title="Pantalla no encontrada"
        subTitle="La pantalla no existe o fue dada de baja."
        extra={
          <Button type="primary" onClick={goBack}>
            Volver al listado
          </Button>
        }
      />
    );
  }

  if (detail.error || !detail.screen) {
    return (
      <Alert
        type="error"
        showIcon
        message="No se pudo cargar la pantalla"
        description={detail.error ?? 'Error inesperado.'}
        action={
          <Button size="small" onClick={detail.refresh}>
            Reintentar
          </Button>
        }
      />
    );
  }

  const screen = detail.screen;

  return (
    <div className="dooh-page">
      <div className="dooh-page__header">
        <Space align="center" size="middle" wrap>
          <Button icon={<ArrowLeftOutlined />} onClick={goBack} aria-label="Volver al listado">
            Volver
          </Button>
          <Typography.Title level={3} style={{ margin: 0 }}>
            {screen.name}
          </Typography.Title>
          <DoohScreenStatusBadge status={detail.status?.status ?? screen.status} />
        </Space>
      </div>

      <Card title="Estado">
        <Descriptions column={{ xs: 1, md: 2 }} size="small">
          <Descriptions.Item label="Último contacto">
            {formatDateTime(detail.status?.lastSeenAt ?? screen.lastSeenAt)}
          </Descriptions.Item>
          <Descriptions.Item label="ID de la pantalla (UUID)">
            {screen.providerDisplayId ?? '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Último medio">
            {detail.status?.lastMediaId ?? '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Resolución">
            {formatResolution(screen.resolutionWidth, screen.resolutionHeight)}
          </Descriptions.Item>
          <Descriptions.Item label="Orientación">
            {screen.orientation ? ORIENTATION_LABELS[screen.orientation] : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Zona horaria">{screen.timezone ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Registrada">{formatDateTime(screen.createdAt)}</Descriptions.Item>
          <Descriptions.Item label="Actualizada">{formatDateTime(screen.updatedAt)}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="Configuración">
        {canManage ? (
          <ScreenConfigForm screen={screen} saving={saving} onSave={(body) => void save(body)} />
        ) : (
          <Alert
            type="warning"
            showIcon
            message="Solo lectura"
            description="Tu rol no permite editar la pantalla. Se requiere SuperAdmin o Soporte N3."
          />
        )}
      </Card>
    </div>
  );
}
